import axios from 'axios'
import { apiClient } from '@/api/client'
import type {
  Reservation,
  ReservationApiErrorBody,
  ReservationApiProblem,
  ReservationCreateInput,
  ReservationListResponse,
  ReservationPause,
  ReservationPauseListResponse,
  ReservationSettings,
  ReservationSettingsInput,
  ReservationStatus,
  ReservationUpdateInput,
} from '../types'

export async function fetchReservations(
  restaurantId: number,
  from: string,
  to: string,
  status?: string,
  search?: string
) {
  const response = await apiClient.get<ReservationListResponse>(
    `/api/admin/restaurants/${restaurantId}/reservations`,
    { params: { from, to, status: status === '' ? undefined : status, q: search === '' ? undefined : search } }
  )
  return response.data
}

export async function fetchReservationSettings(restaurantId: number) {
  const response = await apiClient.get<ReservationSettings>(
    `/api/admin/restaurants/${restaurantId}/reservation-settings`
  )
  return response.data
}

export async function createReservation(
  restaurantId: number,
  input: ReservationCreateInput
) {
  const response = await apiClient.post<Reservation>(
    `/api/admin/restaurants/${restaurantId}/reservations`,
    input
  )
  return response.data
}

export async function updateReservation(
  restaurantId: number,
  reservationId: number,
  input: ReservationUpdateInput
) {
  const response = await apiClient.patch<Reservation>(
    `/api/admin/restaurants/${restaurantId}/reservations/${reservationId}`,
    input,
    { headers: { 'If-Match': `"${input.version}"` } }
  )
  return response.data
}

export async function updateReservationStatus(
  restaurantId: number,
  reservationId: number,
  status: ReservationStatus,
  version: number
) {
  const response = await apiClient.post<Reservation>(
    `/api/admin/restaurants/${restaurantId}/reservations/${reservationId}/status`,
    { status, version },
    { headers: { 'If-Match': `"${version}"` } }
  )
  return response.data
}

export async function saveReservationSettings(
  restaurantId: number,
  input: ReservationSettingsInput
) {
  const response = await apiClient.put<ReservationSettings>(
    `/api/admin/restaurants/${restaurantId}/reservation-settings`,
    input,
    { headers: { 'If-Match': `"${input.version}"` } }
  )
  return response.data
}

export async function fetchReservationPauses(
  restaurantId: number,
  from: string,
  to: string
) {
  const response = await apiClient.get<ReservationPauseListResponse>(
    `/api/admin/restaurants/${restaurantId}/reservation-pauses`,
    { params: { from, to } }
  )
  return response.data.pauses
}

export async function createReservationPause(
  restaurantId: number,
  input: { startsAt: string; endsAt: string; reason?: string }
) {
  const response = await apiClient.post<ReservationPause>(
    `/api/admin/restaurants/${restaurantId}/reservation-pauses`,
    input
  )
  return response.data
}

export async function liftReservationPause(restaurantId: number, pauseId: number) {
  await apiClient.delete(
    `/api/admin/restaurants/${restaurantId}/reservation-pauses/${pauseId}`
  )
}

export function reservationApiProblem(error: unknown): ReservationApiProblem | null {
  if (!axios.isAxiosError<ReservationApiErrorBody>(error)) return null
  return error.response?.data.error ?? null
}
